import { cn, RadioGroupButtonItem, RadioGroupButtons } from "@tellescope/ui";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CustomDot } from "./CustomDot";
import type { ChartDataPoint, useVitalsChart } from "./useVitalsChart";
import { getVitalsClassificationTranslations } from "../../../../../constants";
import { i18n } from "@tellescope/i18n";

const UNIT_LINE_COLOR_MAP: Record<string, string> = {
  SBP: "#ef4444",
  DBP: "#3b82f6",
  BPM: "#8b5cf6",
};

const getUnitToDisplayUnitMap = (): Record<
  string,
  {
    lead?: string;
    trailing?: string;
  }
> => ({
  SBP: { lead: i18n.t("SBP", { ns: "vitals" }), trailing: i18n.t("mmHg", { ns: "vitals" }) },
  DBP: { lead: i18n.t("DBP", { ns: "vitals" }), trailing: i18n.t("mmHg", { ns: "vitals" }) },
  BPM: { lead: i18n.t("Heart Rate (BPM)", { ns: "vitals" }) },
  LB: { trailing: i18n.t("lbs", { ns: "vitals" }) },
  "mg/dL": { lead: i18n.t("Glucose (mg/dL)", { ns: "vitals" }) },
  Height: { trailing: i18n.t("in", { ns: "vitals" }) },
});

export interface VitalsChartProps extends ReturnType<typeof useVitalsChart> {
  className?: string;
  height?: string;
}

export const VitalsChart = ({ className, height, ...rest }: VitalsChartProps) => {
  const { t } = useTranslation("vitals");
  const {
    chartData,
    thresholdLines,
    legendItems,
    yDomain,
    xDomain,
    xTicks,
    formatXTick,
    activeUnit,
    availableUnits,
    selectedUnit,
    setSelectedUnit,
    selectedObservationId,
    selectedObservationTimestamp,
    onDotClick,
    showUnitSelector,
  } = rest;

  const selectedObservationIdRef = useRef(selectedObservationId);
  selectedObservationIdRef.current = selectedObservationId;

  const hasAnimatedRef = useRef(false);
  const prevActiveUnitRef = useRef(activeUnit);
  if (prevActiveUnitRef.current !== activeUnit) {
    prevActiveUnitRef.current = activeUnit;
    hasAnimatedRef.current = false;
  }

  const renderDot = useCallback(
    (props: { cx?: number; cy?: number; payload?: ChartDataPoint }) => (
      <CustomDot {...props} selectedObservationId={selectedObservationIdRef.current} onDotClick={onDotClick} />
    ),
    [onDotClick]
  );

  if (!chartData.length) {
    return (
      <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">
        {t("No data for this period")}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {legendItems.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 px-1">
          {legendItems.map((item) => (
            <div key={item.classification} className="flex items-center gap-1">
              <svg width="16" height="2">
                <line x1="0" y1="1" x2="16" y2="1" stroke={item.color} strokeWidth="2" strokeDasharray="3 3" />
              </svg>
              <span className="text-xs text-muted-foreground">
                {getVitalsClassificationTranslations()[item.classification]}
                {item.rangeLabel ? `: ${item.rangeLabel}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
      {showUnitSelector && (
        <div className="px-1">
          <RadioGroupButtons
            value={selectedUnit}
            onValueChange={setSelectedUnit}
            className="flex items-center gap-0"
            selectedVariant="ghost"
          >
            {availableUnits.map((unit) => (
              <RadioGroupButtonItem
                key={unit}
                value={unit}
                className={cn("px-2 text-xs h-6 font-normal gap-0.5", selectedUnit !== unit && "opacity-30")}
              >
                <div
                  className="h-[2px] rounded-full w-[12px]"
                  style={{ backgroundColor: UNIT_LINE_COLOR_MAP[unit] ?? "#000000" }}
                />
                {unit}
              </RadioGroupButtonItem>
            ))}
          </RadioGroupButtons>
        </div>
      )}
      <div style={{ height: height || "25svh" }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis
              dataKey="timestamp"
              type="number"
              domain={xDomain}
              ticks={xTicks}
              tickFormatter={formatXTick}
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              padding="no-gap"
            />
            <YAxis
              domain={yDomain}
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip
              content={({ active, payload: tooltipPayload }) => {
                if (!active || !tooltipPayload?.length) return null;
                const point = tooltipPayload[0]?.payload as ChartDataPoint | undefined;
                if (!point) return null;
                const date = new Date(point.timestamp);
                const dateStr = date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
                const timeStr = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
                const { lead = "", trailing = "" } = getUnitToDisplayUnitMap()[activeUnit] ?? {
                  lead: "",
                  trailing: activeUnit,
                };

                return (
                  <div className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs shadow-md space-y-1">
                    <div className="text-muted-foreground">
                      {dateStr} {timeStr}
                    </div>
                    <div className="font-medium" style={{ color: point.color }}>
                      {lead
                        ? t("{{lead}}: {{value}} {{trailing}}", { lead, trailing, value: point.value }).trim()
                        : t("{{value}} {{trailing}}", { value: point.value, trailing })}
                    </div>
                  </div>
                );
              }}
            />
            {thresholdLines.map((line, index) => (
              <ReferenceLine
                // eslint-disable-next-line react/no-array-index-key
                key={`${line.classification}-${line.value}-${index}`}
                y={line.value}
                stroke={line.color}
                strokeDasharray="4 4"
                strokeOpacity={0.7}
              />
            ))}
            <ReferenceLine
              x={selectedObservationTimestamp ?? 0}
              stroke="#000000"
              strokeDasharray="4 4"
              strokeWidth={1}
              strokeOpacity={selectedObservationTimestamp !== undefined ? 1 : 0}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={UNIT_LINE_COLOR_MAP[activeUnit] ?? "#111827"}
              strokeWidth={2}
              dot={renderDot}
              activeDot={false}
              isAnimationActive={!hasAnimatedRef.current}
              onAnimationEnd={() => {
                hasAnimatedRef.current = true;
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
